import { Component, Signal, computed, inject, input, signal } from "@angular/core";
import { CommonModule, NgClass } from "@angular/common";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatButtonModule } from "@angular/material/button";

import {
  VaultExplorerService,
  VaultItemGroup,
} from "@bitwarden/web-vault/app/services/dali/vault-explorer.service";
import {
  StoreModelNameByVaultCode,
  VaultCodeEnum,
} from "@bitwarden/web-vault/app/services/dali/type/dali.type";
import { StoreData } from "@bitwarden/web-vault/app/models/store/store.data";
import { TransactionStoreModel } from "@bitwarden/web-vault/app/models/store/transaction.store.model";
import { SourceTransactionStoreModel } from "@bitwarden/web-vault/app/models/store/source-transaction.store.model";
import { SourceTransactionsView } from "@bitwarden/web-vault/app/models/view/source-transactions/source-transactions.view";
import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";
import { enrichTransaction } from "@bitwarden/web-vault/app/models/view/transaction/transaction.utils";
import { ImportStoreService } from "@bitwarden/web-vault/app/services/store/import/import.store.service";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { ConfirmationEnum } from "@bitwarden/web-vault/app/models/enum/confirmation.enum";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import { VaultJsonObjectDialogComponent } from "../../vault-json-object-dialog.component";
import { VaultSameSourceTransactionsDialogComponent } from "../../vault-same-source-transactions-dialog.component";
import { VaultSameIdTransactionsDialogComponent } from "../../vault-same-id-transactions-dialog.component";
import {
  VaultCleanupResultDialogComponent,
  CleanupGroupResult,
  KeptSourceEntry,
} from "../../vault-cleanup-result-dialog.component";
import { VaultDedupStyleInfoDialogComponent } from "../../vault-dedup-style-info-dialog.component";
import {
  DedupStyleValue,
  TRANSACTION_VAULT_DEDUP_STYLES,
} from "../../vault-transaction-dedup.constants";
import { VaultDataTypeBase } from "../vault-data-type-base";
import {
  pickBestTransactionDuplicateKeeper,
  pickBestTransactionDuplicateKeeperStyleId,
} from "../../transaction-dedup-keeper";
import { GlossScrollDirective } from "@bitwarden/web-vault/app/components/gloss-scroll/gloss-scroll.directive";

export interface VaultSameSourceGroupRow {
  sourceKey: string;
  srIdMatch: string;
  transactions: TransactionStoreModel[];
}

export interface VaultSameIdGroupRow {
  sharedId: string;
  transactions: TransactionStoreModel[];
}

@Component({
  selector: "app-vault-type-transaction",
  standalone: true,
  imports: [GlossScrollDirective, CommonModule, NgClass, MatCheckboxModule, MatButtonModule],
  templateUrl: "./vault-type-transaction.component.html",
})
export class VaultTypeTransactionComponent extends VaultDataTypeBase {
  private importStore = inject(ImportStoreService);
  private transactionStore = inject(TransactionStoreService);

  readonly vaultType = input.required<VaultCodeEnum>();
  protected readonly _vaultType: Signal<VaultCodeEnum> = this.vaultType;

  override readonly vaultDuplicatesOnly = signal(true);

  readonly vaultDescriptionSearch = signal("");
  readonly vaultShowTransactionsWithoutSourceOnly = signal(false);
  readonly vaultSameSourceGroupingActive = signal(false);
  readonly vaultShowNonValidSourceTxOnly = signal(false);
  readonly vaultSameIdGroupingActive = signal(false);

  /** Currently selected dedup style for hashing. */
  readonly selectedDedupStyle = signal<DedupStyleValue>("A");
  readonly dedupStyles = TRANSACTION_VAULT_DEDUP_STYLES;

  readonly selectedDedupStyleDescription = computed(() => {
    const style = TRANSACTION_VAULT_DEDUP_STYLES.find((s) => s.value === this.selectedDedupStyle());
    return style?.description ?? "";
  });

  /** Cache: grouped items per style. Cleared when underlying data changes. */
  private _groupCache = new Map<DedupStyleValue, VaultItemGroup[]>();
  private _cachedItemsRef: StoreData[] | null = null;

  override readonly vaultItemGroups = computed((): VaultItemGroup[] => {
    const type = this._vaultType();
    if (!type) {
      return [];
    }
    const items = this.vaultExplorer.getItemsByType(type);

    if (items !== this._cachedItemsRef) {
      this._groupCache.clear();
      this._cachedItemsRef = items;
    }

    const style = this.selectedDedupStyle();
    const cached = this._groupCache.get(style);
    if (cached) {
      return cached;
    }

    const groups =
      style === "ID"
        ? this.vaultExplorer.groupItemsByTransactionId(items)
        : this.duplicatesService.getGroupedItemsCustom(
            type,
            TRANSACTION_VAULT_DEDUP_STYLES.find((s) => s.value === style)!.excludeKeys,
          );
    this._groupCache.set(style, groups);
    return groups;
  });

  onDedupStyleChange(ev: Event): void {
    const el = ev.target as HTMLSelectElement;
    this.selectedDedupStyle.set(el.value as DedupStyleValue);
  }

  openDedupStyleInfoDialog(): void {
    this.dialog.open(VaultDedupStyleInfoDialogComponent, {
      width: "min(700px, 95vw)",
      maxHeight: "90vh",
      data: { selectedStyle: this.selectedDedupStyle() },
    });
  }

  readonly vaultSameSourceRawItems = computed((): TransactionStoreModel[] => {
    let items = this.vaultExplorer.getItemsByType(
      VaultCodeEnum.Transaction,
    ) as TransactionStoreModel[];
    if (this.vaultShowTransactionsWithoutSourceOnly()) {
      items = items.filter((i) => this.transactionHasNoLinkedSource(i as StoreData));
    }
    const q = this.vaultDescriptionSearch().trim();
    if (q) {
      items = items.filter((i) => this.matchesDescriptionSearch(i as StoreData));
    }
    return items;
  });

  readonly vaultTransactionsGroupedBySameSource = computed((): VaultSameSourceGroupRow[] => {
    if (!this.vaultSameSourceGroupingActive()) {
      return [];
    }
    return this.groupBySrId(this.vaultSameSourceRawItems()).filter(
      (g) => g.transactions.length > 1,
    );
  });

  readonly vaultMultipleSameSourceGroupsExist = computed(() =>
    this.groupBySrId(this.vaultSameSourceRawItems()).some((g) => g.transactions.length > 1),
  );

  readonly vaultTransactionsGroupedBySameId = computed((): VaultSameIdGroupRow[] => {
    if (!this.vaultSameIdGroupingActive()) {
      return [];
    }
    return this.groupById(this.vaultSameSourceRawItems()).filter((g) => g.transactions.length > 1);
  });

  readonly vaultMultipleSameIdGroupsExist = computed(() =>
    this.groupById(this.vaultSameSourceRawItems()).some((g) => g.transactions.length > 1),
  );

  override readonly vaultTotalItems = computed(() => {
    if (this.vaultSameSourceGroupingActive()) {
      return this.vaultTransactionsGroupedBySameSource().reduce(
        (sum, g) => sum + g.transactions.length,
        0,
      );
    }
    if (this.vaultSameIdGroupingActive()) {
      return this.vaultTransactionsGroupedBySameId().reduce(
        (sum, g) => sum + g.transactions.length,
        0,
      );
    }
    return this.vaultDisplayedItemGroups().reduce((sum, g) => sum + g.count, 0);
  });

  override readonly vaultDisplayedUniqueCount = computed(() => {
    if (this.vaultSameSourceGroupingActive()) {
      return this.vaultTransactionsGroupedBySameSource().length;
    }
    if (this.vaultSameIdGroupingActive()) {
      return this.vaultTransactionsGroupedBySameId().length;
    }
    return this.vaultDisplayedItemGroups().length;
  });

  protected override filterGroups(groups: VaultItemGroup[]): VaultItemGroup[] {
    groups = super.filterGroups(groups);
    if (this.vaultShowTransactionsWithoutSourceOnly()) {
      groups = groups.filter((g) => this.transactionHasNoLinkedSource(g.representative));
    }
    if (this.vaultShowNonValidSourceTxOnly()) {
      groups = groups.filter((g) => this.hasSrIdButNoSourceRow(g.representative));
    }
    if (this.vaultDescriptionSearch().trim() !== "") {
      groups = groups.filter((g) => this.matchesDescriptionSearch(g.representative));
    }
    return groups;
  }

  onDescriptionSearchInput(ev: Event): void {
    const el = ev.target as HTMLInputElement | null;
    this.vaultDescriptionSearch.set(el?.value ?? "");
  }

  toggleTransactionsWithoutSourceFilter(): void {
    this.vaultShowTransactionsWithoutSourceOnly.update((v) => !v);
  }

  toggleSameSourceGrouping(): void {
    const next = !this.vaultSameSourceGroupingActive();
    if (next) {
      this.vaultShowNonValidSourceTxOnly.set(false);
      this.vaultSameIdGroupingActive.set(false);
    }
    this.vaultSameSourceGroupingActive.set(next);
  }

  toggleNonValidSourceTxOnly(): void {
    const next = !this.vaultShowNonValidSourceTxOnly();
    if (next) {
      this.vaultSameSourceGroupingActive.set(false);
      this.vaultSameIdGroupingActive.set(false);
    }
    this.vaultShowNonValidSourceTxOnly.set(next);
  }

  toggleSameIdGrouping(): void {
    const next = !this.vaultSameIdGroupingActive();
    if (next) {
      this.vaultSameSourceGroupingActive.set(false);
      this.vaultShowNonValidSourceTxOnly.set(false);
    }
    this.vaultSameIdGroupingActive.set(next);
  }

  openTransactionDetails(group: VaultItemGroup): void {
    this.dialog.open(VaultJsonObjectDialogComponent, {
      width: "min(900px, 95vw)",
      maxHeight: "90vh",
      data: { title: "Vault transaction", item: group.representative },
    });
  }

  openSameSourceTransactionDetails(group: VaultSameSourceGroupRow): void {
    const first = group.transactions[0];
    if (!first) {
      return;
    }
    this.dialog.open(VaultJsonObjectDialogComponent, {
      width: "min(900px, 95vw)",
      maxHeight: "90vh",
      data: { title: "Vault transaction", item: first as StoreData },
    });
  }

  openSameSourceAppTransactionsModal(group: VaultSameSourceGroupRow): void {
    const txs = this.collectTransactionViewsForSameSourceGroup(group);
    this.dialog.open(VaultSameSourceTransactionsDialogComponent, {
      width: "min(900px, 95vw)",
      maxHeight: "90vh",
      data: { srIdLabel: group.sourceKey, transactions: txs },
    });
  }

  openSameIdTransactionsModal(group: VaultSameIdGroupRow): void {
    this.dialog.open(VaultSameIdTransactionsDialogComponent, {
      width: "min(900px, 95vw)",
      maxHeight: "90vh",
      data: { sharedId: group.sharedId, transactions: group.transactions },
    });
  }

  sameSourceGroupSummary(group: VaultSameSourceGroupRow): string {
    return this.groupRepSummary(group.transactions[0]);
  }

  sameIdGroupSummary(group: VaultSameIdGroupRow): string {
    return this.groupRepSummary(group.transactions[0]);
  }

  override async removeAllDuplicates(): Promise<void> {
    const type = this._vaultType();
    if (!type || this.vaultDuplicateGroups().length === 0) {
      return;
    }
    const groupCount = this.vaultDuplicateGroups().length;
    const dupRecords = this.vaultTotalDuplicateRecordsToRemove();
    const confirmed = await this.confirmationService.confirmFor(
      ConfirmationEnum.removeAllVaultDuplicatesForType,
      {
        messageReplacement: `Remove ${dupRecords} duplicate record(s) across ${groupCount} group(s)? The latest copy with a valid source per group is kept.`,
      },
    );
    if (!confirmed) {
      return;
    }

    const style = this.selectedDedupStyle();
    const allDupGroups = (
      style === "ID"
        ? this.vaultExplorer.groupItemsByTransactionId(this.vaultExplorer.getItemsByType(type))
        : this.duplicatesService.getGroupedItemsCustom(
            type,
            TRANSACTION_VAULT_DEDUP_STYLES.find((s) => s.value === style)!.excludeKeys,
          )
    ).filter((g) => g.count > 1);

    await this.executeCleanupForGroups(type, allDupGroups);
  }

  override async onItemClick(group: VaultItemGroup): Promise<void> {
    if (group.count <= 1) {
      return;
    }
    const type = this._vaultType();
    if (!type) {
      return;
    }

    const confirmed = await this.confirmationService.confirmFor(
      ConfirmationEnum.deletingVaultDuplicates,
      {
        messageReplacement: `This item has ${group.count} copies. Delete ${group.count - 1} duplicate(s) and keep the latest with a valid source?`,
      },
    );
    if (!confirmed) {
      return;
    }

    await this.executeCleanupForGroups(type, [group]);
  }

  override async performCleanup(): Promise<void> {
    const type = this._vaultType();
    if (!type) {
      return;
    }
    const allGroups = this.vaultDuplicateGroups();
    if (allGroups.length === 0) {
      return;
    }
    const count = this.vaultCleanupCount() ?? allGroups.length;
    const groupsToClean = allGroups.slice(0, count);

    await this.executeCleanupForGroups(type, groupsToClean);
  }

  /**
   * Shared cleanup logic used by performCleanup, removeAllDuplicates, and onItemClick.
   *
   * For each group:
   *  1. Pick which copy to keep: Style-ID prefers newest-by-dc among rows with valid srId and a
   *     non-"Uncategorized" allocation ({@link pickBestToKeepStyleId}); other styles use
   *     {@link pickBestToKeep} (valid srId first, else newest).
   *  2. Delete all other copies.
   *  3. Collect srIds of deleted transactions (excluding the kept one's srId).
   *  4. Remove from that list any srId still referenced by a surviving transaction.
   *  5. Delete source transactions whose ids remain in the list.
   *  6. Show the cleanup result modal with a source transaction section.
   */
  private async executeCleanupForGroups(
    type: VaultCodeEnum,
    groups: VaultItemGroup[],
  ): Promise<void> {
    const allSourceTxs = this.vaultExplorer.getCachedItemsByType(VaultCodeEnum.SourceTransaction);
    const sourceIdSet = new Set(allSourceTxs.map((s) => String(s.id ?? "").trim()).filter(Boolean));

    const results: CleanupGroupResult[] = [];
    const allTxToDelete: StoreData[] = [];
    const keptSrIds = new Set<string>();
    const candidateOrphanSrIds = new Set<string>();

    for (const group of groups) {
      if (group.count <= 1) {
        continue;
      }

      const sorted = [...group.all].sort(
        (a, b) => new Date(b.dc).getTime() - new Date(a.dc).getTime(),
      );

      const kept =
        this.selectedDedupStyle() === "ID"
          ? pickBestTransactionDuplicateKeeperStyleId(sorted, sourceIdSet)
          : pickBestTransactionDuplicateKeeper(sorted, sourceIdSet);
      const deleted = sorted.filter((item) => item !== kept);

      results.push({ kept, deleted });
      allTxToDelete.push(...deleted);

      const keptSrId = String((kept as TransactionStoreModel).srId ?? "").trim();
      if (keptSrId) {
        keptSrIds.add(keptSrId);
      }

      for (const del of deleted) {
        const delSrId = String((del as TransactionStoreModel).srId ?? "").trim();
        if (delSrId) {
          candidateOrphanSrIds.add(delSrId);
        }
      }
    }

    if (allTxToDelete.length === 0) {
      return;
    }

    for (const id of keptSrIds) {
      candidateOrphanSrIds.delete(id);
    }

    const deletedTxIds = new Set(allTxToDelete.map((d) => String(d.id ?? "").trim()));
    const allTransactions = this.vaultExplorer.getCachedItemsByType(VaultCodeEnum.Transaction);

    for (const srId of [...candidateOrphanSrIds]) {
      const stillReferenced = allTransactions.some((tx) => {
        const txId = String(tx.id ?? "").trim();
        const txSrId = String((tx as TransactionStoreModel).srId ?? "").trim();
        return txSrId === srId && !deletedTxIds.has(txId);
      });
      if (stillReferenced) {
        candidateOrphanSrIds.delete(srId);
      }
    }

    const deletedSourceTxs: StoreData[] = [];

    for (const srcTx of allSourceTxs) {
      const srcId = String(srcTx.id ?? "").trim();
      if (!srcId || !candidateOrphanSrIds.has(srcId)) {
        continue;
      }
      deletedSourceTxs.push(srcTx);
    }

    const keptSourceTxs: KeptSourceEntry[] = [];
    for (const keptSrId of keptSrIds) {
      const srcTx = allSourceTxs.find((s) => String(s.id ?? "").trim() === keptSrId);
      if (srcTx) {
        const linkedTxs = allTransactions.filter((tx) => {
          const txSrId = String((tx as TransactionStoreModel).srId ?? "").trim();
          const txId = String(tx.id ?? "").trim();
          return txSrId === keptSrId && !deletedTxIds.has(txId);
        });
        keptSourceTxs.push({ source: srcTx, linkedTransactions: linkedTxs });
      }
    }

    try {
      const modelName = StoreModelNameByVaultCode[type];
      await this.vaultExplorer.deleteVaultItems(modelName, allTxToDelete);
      if (deletedSourceTxs.length > 0) {
        const srcModelName = StoreModelNameByVaultCode[VaultCodeEnum.SourceTransaction];
        await this.vaultExplorer.deleteVaultItems(srcModelName, deletedSourceTxs);
      }
      await this.vaultExplorer.reloadVaultDataForType(type);
      if (deletedSourceTxs.length > 0) {
        await this.vaultExplorer.reloadVaultDataForType(VaultCodeEnum.SourceTransaction);
      }
      this.clearGroupCache();
    } catch {
      this.confirmationService.notify(NotificationEnum.error, "error", "Cleanup failed.", false);
      return;
    }

    this.dialog.open(VaultCleanupResultDialogComponent, {
      width: "min(1100px, 95vw)",
      maxHeight: "90vh",
      data: {
        groups: results,
        deletedSourceTransactions: deletedSourceTxs,
        keptSourceTransactions: keptSourceTxs,
      },
    });
  }

  private clearGroupCache(): void {
    this._groupCache.clear();
    this._cachedItemsRef = null;
  }

  // ── Private helpers ──

  private groupRepSummary(rep: TransactionStoreModel | undefined): string {
    if (!rep) {
      return "—";
    }
    try {
      const { id: _id, vid: _vid, dc: _dc, dm: _dm, v: _v, del: _del, ...rest } = rep as any;
      return JSON.stringify(rest, null, 2);
    } catch {
      return "—";
    }
  }

  private sortSameGroup(txs: TransactionStoreModel[]): TransactionStoreModel[] {
    return [...txs].sort((a, b) => {
      const ta = Date.parse(String(a.dc ?? "")) || 0;
      const tb = Date.parse(String(b.dc ?? "")) || 0;
      if (ta !== tb) {
        return ta - tb;
      }
      return String(a.id ?? "").localeCompare(String(b.id ?? ""));
    });
  }

  private groupBySrId(items: TransactionStoreModel[]): VaultSameSourceGroupRow[] {
    const map = new Map<string, TransactionStoreModel[]>();
    for (const it of items) {
      const srId = String(it.srId ?? "").trim();
      const key = srId || "__empty__";
      const prev = map.get(key);
      if (prev) {
        prev.push(it);
      } else {
        map.set(key, [it]);
      }
    }
    return Array.from(map.entries())
      .map(([key, txs]) => ({
        sourceKey: key === "__empty__" ? "(no srId)" : key,
        srIdMatch: key === "__empty__" ? "" : key,
        transactions: this.sortSameGroup(txs),
      }))
      .sort((a, b) => a.sourceKey.localeCompare(b.sourceKey));
  }

  private groupById(items: TransactionStoreModel[]): VaultSameIdGroupRow[] {
    const map = new Map<string, TransactionStoreModel[]>();
    for (const it of items) {
      const id = String(it.id ?? "").trim();
      if (!id) {
        continue;
      }
      const prev = map.get(id);
      if (prev) {
        prev.push(it);
      } else {
        map.set(id, [it]);
      }
    }
    return Array.from(map.entries())
      .map(([sharedId, txs]) => ({
        sharedId,
        transactions: this.sortSameGroup(txs),
      }))
      .sort((a, b) => a.sharedId.localeCompare(b.sharedId));
  }

  private matchesDescriptionSearch(item: StoreData): boolean {
    const q = this.vaultDescriptionSearch().trim().toLowerCase();
    if (!q) {
      return true;
    }
    const dsc = String((item as TransactionStoreModel).dsc ?? "").toLowerCase();
    return dsc.includes(q);
  }

  private hasSrIdButNoSourceRow(item: StoreData): boolean {
    const srId = String((item as TransactionStoreModel).srId ?? "").trim();
    if (!srId) {
      return false;
    }
    const sources =
      this.importStore.sourceTransaction.sourceTransactionViews() ??
      ([] as SourceTransactionsView[]);
    return !sources.some((s) => String(s.id ?? "").trim() === srId);
  }

  private transactionHasNoLinkedSource(item: StoreData): boolean {
    const id = String((item as Record<string, unknown>)["id"] ?? "").trim();
    if (!id) {
      return true;
    }
    const sources =
      this.importStore.sourceTransaction.sourceTransactionViews() ??
      ([] as SourceTransactionsView[]);
    return !sources.some((s) => s.sourceId === id);
  }

  private collectTransactionViewsForSameSourceGroup(
    group: VaultSameSourceGroupRow,
  ): TransactionView[] {
    const srId = group.srIdMatch.trim();
    const vaultModelsById = new Map<string, TransactionStoreModel>();
    for (const t of group.transactions) {
      const id = String(t.id ?? "").trim();
      if (id) {
        vaultModelsById.set(id, t);
      }
    }

    const raw = this.transactionStore.transaction.transactionViews() ?? [];
    const enriched = this.transactionStore.enrichTransaction.collection() ?? [];
    const byId = new Map<string, TransactionView>();
    for (const tx of raw) {
      byId.set(tx.id, tx);
    }
    for (const tx of enriched) {
      byId.set(tx.id, tx);
    }

    for (const [id, model] of vaultModelsById) {
      if (!byId.has(id)) {
        const v = new TransactionView(model);
        enrichTransaction(v);
        byId.set(id, v);
      }
    }

    const include = (tx: TransactionView): boolean => {
      const link = (tx.sourceLink?.id ?? tx.sourceId ?? "").trim();
      if (vaultModelsById.has(tx.id)) {
        return true;
      }
      if (srId === "") {
        return false;
      }
      return link === srId;
    };

    return [...byId.values()]
      .filter(include)
      .sort((a, b) => (a.postDate?.getTime() ?? 0) - (b.postDate?.getTime() ?? 0));
  }
}
